/*
 * @Description: utils
 * @Author: zhongshanxian01
 * @Date: 2021-04-15 20:20:07
 * @LastEditors: zhongshanxian01
 * @LastEditTime: 2022-12-16 16:03:18
 */

import { isDev } from '@utils/env';
import { isMC } from '@utils/browser';

declare let window: any;
declare let ClientICC: any;
interface JoinRoomOptions {
    from?: string; // 来源channel or other
    pincode?: string; // pincode
}

/**
 * 对比两个版本号
 * @param ver1
 * @param ver2
 *
 * @returns -1 : ver1 小于 ver2
 *           0 ：ver1 等于 ver2
 *           1 ：ver1 大于 ver2
 *       false ：参数错误
 */
export function compareVersion(ver1: string, ver2: string) {
    if (ver1 && ver2) {
        //将两个版本号拆成数字
        const arr1 = ver1.split('.'),
            arr2 = ver2.split('.');
        const minLength = Math.min(arr1.length, arr2.length);
        let position = 0,
            diff: any = 0;
        //依次比较版本号每一位大小，当对比得出结果后跳出循环
        while (
            position < minLength &&
            (diff = parseInt(arr1[position]) - parseInt(arr2[position]) == 0)
        ) {
            position++;
        }
        diff = diff != 0 ? diff : arr1.length - arr2.length;
        diff = diff > 1 ? 1 : diff;
        diff = diff < -1 ? -1 : diff;
        return diff;
    } else {
        //输入为空
        return false;
    }
}

/**
 * @description: 随机排序
 * @param {*} list 需要排序列表
 * @return {Array} result
 */
export const shuffle = function(list: any[] = []) {
    const result = JSON.parse(JSON.stringify(list));
    let len = result.length || 0;
    if (len == 0) return [];

    while (len) {
        const i = Math.floor(Math.random() * len--);
        const t = result[len];
        result[len] = result[i];
        result[i] = t;
    }
    return result;
};

/**
 * @description: 自定义排序
 * @param {Array} list 需要排序列表
 * @param {String} key 根据key排序，key为空则根据item
 * @param {Number} type 1随机/2降序/3升序
 * @return {Array} result
 */
export const customSort = function(list: any[] = [], key = '', type = 1) {
    const result = JSON.parse(JSON.stringify(list));
    if (type == 1) {
        return shuffle(result);
    } else if (type == 2 || type == 3) {
        return result.sort((a: any, b: any) => {
            let val1 = a;
            let val2 = b;
            if (key) {
                val1 = a[key];
                val2 = b[key];
            }
            switch (type) {
                case 3:
                    return val1 - val2;
                default:
                    return val2 - val1;
            }
        });
    }
    return result;
};

/**
 * @description: 动态加载js
 * @param {string} url
 * @param {Function} callback
 */
export const loadJs = (url: string) => {
    return new Promise<void>((resolve, reject) => {
        let head = document.getElementsByTagName('head')[0];
        let script = document.createElement('script');
        script.type = 'text/javascript';
        script.onload = () => {
            resolve();
            script.onload = null;
        };
        script.onerror = reject;
        script.src= url;
        head.appendChild(script);
    });
}


/**
 * @description: 进入cc app
 * @param {number} roomid
 * @param {number} subcid
 * @param {JoinRoomOptions} options
 * @return {*}
 */
export const joinRoom = async function(roomid = 0, subcid = 0, options?: JoinRoomOptions) {
    const isMobileCC = await ClientICC.isMobileCC();
    const prefixUrl = isDev ? 'https://dev.cc.163.com/act/ulink/intro/index.html' : 'https://ulink.cc.163.com/intro/index.html';
    let url = 'cc://maintab/alive_game';

    if (options?.from == 'channel') {
        // 直播模块跳转处理
        if (options?.pincode) {
            url = `cc://pincode/€${options.pincode}€`;
        } else if (roomid && subcid) {
            url = `cc://join-room/${roomid}/${subcid}`;
        }
        if (isMobileCC) {
            location.href = url;
            return false;
        } else if(isMC()) {
            // #401256 我的世界APP内通过三种页面拉起CCapp-H5/专题页-前端
            loadJs('//mc.163.com/launcher/jssdk/0.6/mc-js-sdk.min.js').then(() => {
                window.mc.openExternalUrl(`${prefixUrl}?action=${url}`);
            }).catch(() => {
                location.href = `${prefixUrl}?action=${url}`;
            });
            return false;
        } else {
            // cc外
            location.href = `${prefixUrl}?action=${url}`;
            return false;
        }
    } else {
        // 榜单模块-应援进入直播间处理
        if (!roomid || !subcid) return;
        if (isMobileCC) {
            // cc-房间外：使用已有sdk
            // cc-房间内：使用已有sdk
            ClientICC.joinRoom(roomid, subcid);
        } else {
            // cc外：使用ulink
            //     pincode形式：'https://ulink.cc.163.com/intro/index.html?action=cc://pincode/€xxxxxxx€'
            //     首页：'https://ulink.cc.163.com/intro/index.html?action=cc://maintab/alive_game'
            //     使用roomid和subcid：'https://ulink.cc.163.com/intro/index.html?action=cc://maintab/alive_game'
            location.href = `${prefixUrl}?action=cc://join-room/${roomid}/${subcid}`;
        }
    }
};

export function changeUrlArg(url: string, arg: string, val: string | number): string {
    let pattern = arg + '=([^&]*)';
    let replaceText = arg + '=' + val;
    return url.match(pattern)
        ? url.replace(eval('/(' + arg + '=)([^&]*)/gi'), replaceText)
        : url.match('[?]')
        ? url + '&' + replaceText
        : url + '?' + replaceText;
}

/**
 * @description: 通过url进入cc app
 * @param {string} url 目的url
 * @return {*}
 */
export const joinCCByUrl = async (url?: string) => {
    const isMobileCC = await ClientICC.isMobileCC();
    const prefixUrl = isDev ? 'https://dev.cc.163.com/act/ulink/intro/index.html' : 'https://ulink.cc.163.com/intro/index.html';
    const defaultUrl = url || 'cc://maintab/alive_game';

    if (isMobileCC) {
        // cc-房间外：使用已有sdk
        // cc-房间内：使用已有sdk
        location.href = defaultUrl;
    } else {
        // cc外：使用ulink
        location.href = `${prefixUrl}?action=${defaultUrl}`;
    }
}

/**
 * @description: 十六进制色值转rgba
 * @param {string} val 十六进制色值
 * @param {number} opacity 透明度
 * @return {string}
 */
export const hexToRgb =  (val: string, opacity = 1) => {   //HEX十六进制颜色值转换为RGB(A)颜色值
    // 16进制颜色值的正则
    var reg = /^#([0-9a-fA-f]{3}|[0-9a-fA-f]{6})$/;
    // 把颜色值变成小写
    var color = val.toLowerCase();
    var result = '';
    if (reg.test(color)) {
        // 如果只有三位的值，需变成六位，如：#fff => #ffffff
        if (color.length === 4) {
            var colorNew = "#";
            for (var i = 1; i < 4; i += 1) {
                colorNew += color.slice(i, i + 1).concat(color.slice(i, i + 1));
            }
            color = colorNew;
        }
        // 处理六位的颜色值，转为RGB
        var colorChange = [];
        for (var i = 1; i < 7; i += 2) {
            colorChange.push(parseInt("0x" + color.slice(i, i + 2)));
        }
        result = "rgba(" + colorChange.join(",") +`,${opacity}`+ ")";
        return result;
    }
    return '';
}


/**
 * @description: 防抖
 * @param {any} func 回调函数
 * @param {number} delay 抖动时间间隔
 * @return {CallableFunction}
 */
 export const debounce = (func: any, delay: number): any => {
    let timer: NodeJS.Timeout;
    if (typeof func !== 'function') return;

    return (...args: any) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func.apply(this, args);
        }, delay);
    };
};